import PlaceTemplatePhoto from 'components/PlaceTemplatePhoto'
import { selectLocationCategories } from './locationSlice'
import { selectFilters } from '../menu/filtersSlice'
import React, { useEffect, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import { useHistory } from 'react-router-dom'
import { displayPrice } from 'utils/string'
import { useSelector } from 'react-redux'
import { useReduxSelector } from 'store'

interface NewPlaceElementForListProps {
  data: locations.Location
  customRedirect?: string
  navigate?: (path: string) => void
}

type Popups = {
  type: 'warehouse_attr' | 'automation'
} | {
  type: 'gallery'
  props: {
    index: number
  }
} | {
  type: 'details'
  props: {
    rentableType?: warehouse.RentableType
    details: any[]
    count?: number
  }
} | {
  type: 'availability'
  props: {
    name: string
    placeId: string
    groupHash: string
    variantHash: string
    companyName: string
  }
}

const NewPlaceElementForList: React.FC<NewPlaceElementForListProps> = ({ data: _, customRedirect, navigate }) => {
  const categories = useReduxSelector(selectLocationCategories)
  const { attributes } = useSelector(selectFilters)
  const [selectedCategoryId, selectCategoryId] = useState(attributes?.categoryId && _.categories?.some(c => c.categoryId === attributes?.categoryId)
    ? attributes?.categoryId
    : (_.categories?.[0]?.categoryId || ''))
  const showGroups = _.displayOptions.sizeGroups && categories[selectedCategoryId]?.rentableType === 'warehouse'
  const [selectedSizeId, selectSizeId] = useState((() => {
    const category = (
      attributes?.categoryId
        ? _.categories?.find(c => c.categoryId === attributes.categoryId)
        : undefined
    ) || _.categories?.[0]
    if (category) {
      if (showGroups) {
        return category.warehouseTypes[0]?.id
          ? _.sizeGroups.find(g => g.warehouseTypeIds.includes(category.warehouseTypes[0]?.id))?.warehouseSizeGroupId
          : ''

      } else {
        return attributes?.warehouseTypeId && category.warehouseTypes.some(type => type.id === attributes?.warehouseTypeId)
          ? attributes.warehouseTypeId
          : (category.warehouseTypes?.[0]?.id || '')
      }
    }
    return ''
  })())

  const category = selectedCategoryId
    ? _.categories?.find(c => c.categoryId === selectedCategoryId)
    : undefined
  const price = selectedSizeId && showGroups
    ? _.sizeGroups?.find(group => group.warehouseSizeGroupId === selectedSizeId)?.monthlyGrossPrice
    : category?.warehouseTypes?.find(type => type.id === selectedSizeId)?.monthlyGrossPrice_min

  useEffect(() => {
    if (selectedCategoryId) {
      const category = _.categories?.find(c => c.categoryId === selectedCategoryId)
      if (category) {
        if (_.sizeGroups && _.sizeGroups.length > 0 && categories[selectedCategoryId]?.rentableType === 'warehouse') {
          selectSizeId(category.warehouseTypes[0]?.id
            ? _.sizeGroups.find(g => g.warehouseTypeIds.includes(category.warehouseTypes[0]?.id))?.warehouseSizeGroupId
            : '')

        } else {
          selectSizeId(attributes?.warehouseTypeId && category.warehouseTypes.some(type => type.id === attributes?.warehouseTypeId)
            ? attributes.warehouseTypeId
            : (category.warehouseTypes?.[0]?.id || ''))
        }
      }
    }
  }, [_.categories, _.sizeGroups, attributes.warehouseTypeId, categories, selectedCategoryId])

  const { push } = useHistory()
  const { t } = useTranslation('map')
  const { openPopup } = usePopupState<Popups>()
  const redirect = () => {
    if (_.isAnnouncement) {
      openPopup({ type: 'announcement', placeId: _.id })
    } else {
      const action = navigate || push
      action(customRedirect
        ? `${customRedirect}${selectedSizeId ? `?warehouseTypeId=${selectedSizeId}` : ''}`
        : `/map/${_.id}`)
    }
  }

  return (
    <div className='placeDetails --listView'>
      <div className='placeDetails__header'>
        <div className='placeDetails__header__content'>
          <div className='placeDetails__header__photo'>
            {_.photos?.[0]?.public_path
              ? <img
                src={_.photos[0].public_path}
                alt=''
              />
              : <PlaceTemplatePhoto />}
            <div className={`placeDetails__badge ${_.constructionDate ? '--warning' : _.isAnnouncement ? '--secondary' : '--primary'}`}>
              {t(_.constructionDate ? 'theYardIsUnderConstruction' : _.isAnnouncement ? 'offer' : 'onlineRental')}
            </div>
          </div>
          <h2>{_.placeName}</h2>
          <p>{_.city}, {_.street} {_.estateNumber}</p>
          {/* {typeof mm_score === 'number' && <MmRating
              number={_?.mm_score}
              action={() => {
                openPopup({ type: 'mm_score', scoreInfo: _.mm_score_info })
              }}
            />} */}
        </div>
      </div>

      {_.place_attrs && _.place_attrs.length > 0 && <div className='placeDetails__attributes__wrapper --border'>
        <div
          className='placeDetails__attributes'
          onClick={() => {
            openPopup({ type: 'warehouse_attr', place_attrs: _.place_attrs })
          }}>
          {_.place_attrs.map((item) => <div
            key={item.id}
            className='placeDetails__attribute'>
            <i className={`icon icon--${item.key}-bold`}></i>
            <span>{item.name}</span>
          </div>)}
        </div>
      </div>}

      <div className='placeDetails__tabs'>
        {_.categories?.map((item, idx) => <div
          key={idx}
          className={`placeDetails__tab${item.categoryId === selectedCategoryId ? ' --selected' : ''}`}
          onClick={() => {
            selectCategoryId(item.categoryId)
          }}>
          {categories[item.categoryId]?.name}
        </div>)}
      </div>

      {category && <div className='placeDetails__automation__wrapper'>
        <div
          className='placeDetails__automation'
          onClick={() => {
            openPopup({ type: 'automation', automation: _.automation })
          }}>
          <div>
            {category.automation?.key === 'full-automatic' && <i className='icon icon--shield-bold'></i>}
            <span className={`${category.automation?.key === 'full-automatic' ? 'green' : ''}`}>{_.automation?.title}</span>
          </div>
          <div>{category.automation?.description}</div>
        </div>
      </div>}

      {selectedSizeId && category && <div className='placeDetails__sizes'>
        {showGroups
          ? _.sizeGroups.map((item, idx) => {
            if (!category.warehouseTypes.some(type => item.warehouseTypeIds.includes(type.id))) return null
            return (
              <div
                key={idx}
                className={`placeDetails__size${item.warehouseSizeGroupId === selectedSizeId ? ' --selected' : ''}`}
                onClick={() => selectSizeId(item.warehouseSizeGroupId)}>
                {item.name}
              </div>
            )
          })
          : _.sizes.map((item, idx) => {
            if (!category.warehouseTypes.some(type => type.id === item.warehouseTypeId)) return null
            return (
              <div
                key={idx}
                className={`placeDetails__size${item.warehouseTypeId === selectedSizeId ? ' --selected' : ''}`}
                onClick={() => selectSizeId(item.warehouseTypeId)}>
                {item.rentableType === 'warehouse'
                  ? item.sizeShortName
                  : item.name}
              </div>
            )
          })}
      </div>}

      {selectedSizeId && <div className='placeDetails__price'>
        <div className='placeDetails__subtitle'>{t('costOfSelectedSize')}</div>
        <span className='--secFont'>{t('from')} {price ? displayPrice(price) : ''}</span>
        <span className='--secFont small'>/ {t('monthly')}</span>
      </div>}

      <div className='placeDetails__footer'>
        <ArrowBtn
          enlarged
          className={_.isAnnouncement ? ' --gray' : ''}
          text={t(_.isAnnouncement ? 'showPhoneNumber' : 'see')}
          action={redirect}
        />
      </div>
    </div>
  )
}

export default NewPlaceElementForList